/**
 * demoMode.js — detect demo.<host> subdomains + expose a session flag.
 *
 * When the browser lands on `demo.<anything>.au` (or `demo.<anything>.com`)
 * the site behaves exactly like the parent domain, but every render can
 * check `isDemoMode()` to show a yellow banner + auto-fill the login
 * screen with a "Sign in as demo user" pill.
 *
 * The parent host is exposed via `strippedHost()` so RootRouter and
 * moduleHost can keep their existing hostname → route logic unchanged.
 */
export function isDemoMode() {
  if (typeof window === "undefined") return false;
  try {
    // 1) URL query flag — lets dev/QA test demo UX on any host:
    //    hustlestadium.au/?as=demo  → persists in sessionStorage.
    const p = new URLSearchParams(window.location.search).get("as");
    if (p === "demo") sessionStorage.setItem("hp-demo-mode", "1");
    if (p === "no-demo") sessionStorage.removeItem("hp-demo-mode");
    if (sessionStorage.getItem("hp-demo-mode") === "1") return true;
  } catch { /* ignore */ }
  const host = (window.location.hostname || "").toLowerCase();
  return host.startsWith("demo.");
}

/** Return the parent host with `demo.` stripped, or the raw host. */
export function strippedHost() {
  if (typeof window === "undefined") return "";
  const host = (window.location.hostname || "").toLowerCase();
  if (host.startsWith("demo.")) return host.slice(5);
  return host;
}

/**
 * Return the module key to pass to /api/auth/demo-login. Derived from
 * the parent host — demo.hustlestadium.au → "stadium",
 * demo.hustleeats.au → "eats", etc. Falls back to "suite".
 */
export function demoModuleKey() {
  const host = strippedHost().replace(/^www\./, "");
  const m = host.match(/^(?:hustle|get)?([a-z]+?)(?:\.com|\.au|\.co)?$/);
  if (m && m[1]) return m[1];
  // Manual overrides for compound domains.
  if (host.includes("plate"))     return "plate";
  if (host.includes("stadium"))   return "stadium";
  if (host.includes("eats"))      return "eats";
  if (host.includes("directory")) return "directory";
  if (host.includes("connect"))   return "connect";
  if (host.includes("reviews"))   return "reviews";
  return "suite";
}
